import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import axios from "axios";
import queryString from 'query-string';
import Swal from 'sweetalert2'
import ReactLoading from 'react-loading';
import config from "../../config"
import { useNetworkState } from 'react-use';
import Footer from './Footer';
import Header from './Header';

const B2B_pricing = () => {
    let userDetail = localStorage.getItem("ship_rocket_user");
    let userData = userDetail ? JSON.parse(userDetail) : {};
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedFromZone, setSelectedFromZone] = useState('');
    const [selectedToZone, setSelectedToZone] = useState('');
    const [filteredPricing, setFilteredPricing] = useState([]);
    const [showVasDetails, setShowVasDetails] = useState(false);
    const [showOdaDetails, setShowOdaDetails] = useState(false);
    const [showHandlingDetails, setShowHandlingDetails] = useState(false);

    useEffect(() => {
        fetchPricingData();
    }, []);

    const fetchPricingData = async () => {
        try {
            setLoading(true);
            const full_api = `${config.apiUrl}/shipment/get_detailed_products_pricing`;
          
            const response = await axios.post(full_api, {customer_id: userData.customer_id}, { headers: config.headers });
        
           
            if (response.data.status === "success") {
                setProducts(response.data.products);
                if (response.data.products.length > 0) {
                    setSelectedProduct(response.data.products[0].product_id);
                }
            } else {
                setError(response.data.message || "Failed to fetch pricing data");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch pricing data");
            console.error("Error fetching pricing:", err);
        } finally {
            setLoading(false);
        }
    };

    // Get unique zones from all products
    const getAllZones = () => {
        const zones = new Set();
        products.forEach(product => {
            product.zone_pricing.forEach(zone => {
                zones.add(zone.from_zone.zone_id);
                zones.add(zone.to_zone.zone_id);
            });
        });
        return Array.from(zones).sort();
    };

    // Filter pricing based on selections
    useEffect(() => {
        if (selectedProduct && products.length > 0) {
            const product = products.find(p => p.product_id === selectedProduct);
            if (product) {
                let filtered = product.zone_pricing;
                
                if (selectedFromZone) {
                    filtered = filtered.filter(zone => zone.from_zone.zone_id === selectedFromZone);
                }
                
                if (selectedToZone) {
                    filtered = filtered.filter(zone => zone.to_zone.zone_id === selectedToZone);
                }
                
                setFilteredPricing(filtered);
            }
        }
    }, [selectedProduct, selectedFromZone, selectedToZone, products]);

    const getZoneName = (zoneId) => {
        const product = products.find(p => p.product_id === selectedProduct);
        if (product) {
            const zone = product.zone_pricing.find(z => 
                z.from_zone.zone_id === zoneId || z.to_zone.zone_id === zoneId
            );
            return zone ? (zone.from_zone.zone_id === zoneId ? zone.from_zone.zone_name : zone.to_zone.zone_name) : zoneId;
        }
        return zoneId;
    };

    // Extract pricing data from the complex MongoDB response structure
    const extractPricingData = (slab) => {
        // Handle the MongoDB document structure
        const data = slab._doc || slab;
        return {
            start_range: data.start_range,
            end_range: data.end_range,
            price: data.price,
            additional_price: data.additional_price,
            additional_weight: slab.additional_weight || 1
        };
    };

    // Check if slab is for additional weight
    const isAdditionalWeightSlab = (slab) => {
        const data = slab._doc || slab;
        return data.start_range === null && data.end_range === null;
    };

    // Check if slab is for base weight range
    const isBaseWeightSlab = (slab) => {
        const data = slab._doc || slab;
        return data.start_range !== null && data.end_range !== null && data.price !== null;
    };

    // Format weight range display
    const formatWeightRange = (slab) => {
        const data = slab._doc || slab;
        if (isAdditionalWeightSlab(slab)) {
            return "Additional per kg";
        }
        return `${data.start_range} - ${data.end_range} kg`;
    };

    // Format charge display for VAS, ODA, and handling charges
    const formatChargeDisplay = (charge) => {
        if (charge.charge_per_kg === 0) {
            return `Fixed: ₹${charge.min_charge}`;
        } else {
            return `₹${charge.charge_per_kg}/kg (Min: ₹${charge.min_charge})`;
        }
    };

    // Get current product details
    const getCurrentProduct = () => {
        return products.find(p => p.product_id === selectedProduct);
    };

    if (loading) {
        return (
            <div>
                <Header />
                <div className="page-wrapper pinned">
                    <div className="main-container">
                        <div className='app-container'>
                            <div className="app-body">
                                <div className="container-fluid text-center py-5">
                                    {/* <ReactLoading type="spin" color="#007bff" height={50} width={50} /> */}
                                    <p className="mt-3">Loading pricing data...</p>
                                </div>
                            </div>
                        </div>
                        <Footer />
                    </div>
                </div>
               
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Header />
                <div className="page-wrapper pinned">
                    <div className="main-container">
                        <div className='app-container'>
                            <div className="app-body">
                                <div className="container-fluid text-center py-5">
                                    <div className="alert alert-danger">
                                        <h4>Error</h4>
                                        <p>{error}</p>
                                        <button className="btn btn-primary mt-3" onClick={fetchPricingData}>
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Footer />
                    </div>
                </div>
                
            </div>
        );
    }

    const currentProduct = getCurrentProduct();

    return (
        <div>
            <Header />
            <div className="page-wrapper pinned">
                <div className="main-container">
                    <div className='app-container'>
                        <div className="app-body">
                            <div className="container-fluid">
                                {/* Header Section */}
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="page-title-box">
                                            <h4 className="page-title">B2B Pricing</h4>
                                            <p className="text-muted mb-0">
                                                View and compare pricing across different products and zones
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Filters Section */}
                                <div className="row mb-4">
                                    <div className="col-md-3">
                                        <label className="form-label">Select Product</label>
                                        <select 
                                            className="form-select"
                                            value={selectedProduct || ''}
                                            onChange={(e) => setSelectedProduct(e.target.value)}
                                        >
                                            {/* <option value="">All Products</option> */}
                                            {products.map(product => (
                                                <option key={product.product_id} value={product.product_id}>
                                                    {product.product_name} 
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">From Zone</label>
                                        <select 
                                            className="form-select"
                                            value={selectedFromZone}
                                            onChange={(e) => setSelectedFromZone(e.target.value)}
                                        >
                                            <option value="">All Zones</option>
                                            {getAllZones().map(zoneId => (
                                                <option key={`from-${zoneId}`} value={zoneId}>
                                                    {getZoneName(zoneId)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">To Zone</label>
                                        <select 
                                            className="form-select"
                                            value={selectedToZone}
                                            onChange={(e) => setSelectedToZone(e.target.value)}
                                        >
                                            <option value="">All Zones</option>
                                            {getAllZones().map(zoneId => (
                                                <option key={`to-${zoneId}`} value={zoneId}>
                                                    {getZoneName(zoneId)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 d-flex align-items-end">
                                        <button 
                                            className="btn btn-outline-secondary w-100"
                                            onClick={() => {
                                                setSelectedFromZone('');
                                                setSelectedToZone('');
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>

                                {/* Additional Charges Section */}
                                {currentProduct && (
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h5 className="card-title mb-0">
                                                        Additional Charges - {currentProduct.product_name}
                                                    </h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        {/* VAS Charges */}
                                                        <div className="col-md-4 mb-3">
                                                            <div className="card h-100">
                                                                <div className="card-header bg-light">
                                                                    <h6 className="card-title mb-0 d-flex justify-content-between align-items-center">
                                                                        VAS Charges
                                                                        <button 
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => setShowVasDetails(!showVasDetails)}
                                                                        >
                                                                            {showVasDetails ? 'Hide' : 'Show'}
                                                                        </button>
                                                                    </h6>
                                                                </div>
                                                                <div className="card-body">
                                                                    {showVasDetails ? (
                                                                        <div className="small">
                                                                            <div><strong>FSC Charge:</strong> {currentProduct.vas_charges.fsc_charge}%</div>
                                                                            <div><strong>Carrier Risk:</strong> {currentProduct.vas_charges.carrier_risk_percentage}% or ₹{currentProduct.vas_charges.carrier_risk_amount}</div>
                                                                            <div><strong>Owner Risk:</strong> ₹{currentProduct.vas_charges.owner_risk}</div>
                                                                            <div><strong>COD:</strong> {currentProduct.vas_charges.cod_percentage}% or ₹{currentProduct.vas_charges.cod_amount}</div>
                                                                            <div><strong>Waybill Charge:</strong> ₹{currentProduct.vas_charges.waybill_charge}</div>
                                                                            <div><strong>Tech Charge:</strong> ₹{currentProduct.vas_charges.tech_charge}</div>
                                                                            <div><strong>FM Cost:</strong> ₹{currentProduct.vas_charges.fm_cost_per_kg}/kg or ₹{currentProduct.vas_charges.fm_cost_lr}</div>
                                                                            <div><strong>Green Tax:</strong> ₹{currentProduct.vas_charges.green_tax_per_kg}/kg or ₹{currentProduct.vas_charges.green_tax_lr}</div>
                                                                            <div><strong>Destination Charge:</strong> ₹{currentProduct.vas_charges.destination_charge}/kg</div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center">
                                                                            <div className="h4 text-primary">{Object.keys(currentProduct.vas_charges).length}</div>
                                                                            <small className="text-muted">VAS Services</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* ODA Charges */}
                                                        <div className="col-md-4 mb-3">
                                                            <div className="card h-100">
                                                                <div className="card-header bg-light">
                                                                    <h6 className="card-title mb-0 d-flex justify-content-between align-items-center">
                                                                        ODA Charges
                                                                        <button 
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => setShowOdaDetails(!showOdaDetails)}
                                                                        >
                                                                            {showOdaDetails ? 'Hide' : 'Show'}
                                                                        </button>
                                                                    </h6>
                                                                </div>
                                                                <div className="card-body">
                                                                    {showOdaDetails ? (
                                                                        currentProduct.oda_charges && currentProduct.oda_charges.length > 0 ? (
                                                                            <div className="small">
                                                                                {currentProduct.oda_charges.map((charge, index) => (
                                                                                    <div key={index} className="mb-2">
                                                                                        <strong>
                                                                                            {charge.range_min === null ? 'Up to' : charge.range_min} 
                                                                                            {charge.range_max === null ? '+' : ` - ${charge.range_max}`} kg:
                                                                                        </strong>
                                                                                        <br/>
                                                                                        {formatChargeDisplay(charge)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-muted small">No ODA charges configured</div>
                                                                        )
                                                                    ) : (
                                                                        <div className="text-center">
                                                                            <div className="h4 text-warning">{currentProduct.oda_charges?.length || 0}</div>
                                                                            <small className="text-muted">ODA Slabs</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Handling Charges */}
                                                        <div className="col-md-4 mb-3">
                                                            <div className="card h-100">
                                                                <div className="card-header bg-light">
                                                                    <h6 className="card-title mb-0 d-flex justify-content-between align-items-center">
                                                                        Handling Charges
                                                                        <button 
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => setShowHandlingDetails(!showHandlingDetails)}
                                                                        >
                                                                            {showHandlingDetails ? 'Hide' : 'Show'}
                                                                        </button>
                                                                    </h6>
                                                                </div>
                                                                <div className="card-body">
                                                                    {showHandlingDetails ? (
                                                                        currentProduct.handling_charges && currentProduct.handling_charges.length > 0 ? (
                                                                            <div className="small">
                                                                                {currentProduct.handling_charges.map((charge, index) => (
                                                                                    <div key={index} className="mb-2">
                                                                                        <strong>
                                                                                            {charge.range_min === null ? 'Up to' : charge.range_min} 
                                                                                            {charge.range_max === null ? '+' : ` - ${charge.range_max}`} kg:
                                                                                        </strong>
                                                                                        <br/>
                                                                                        {formatChargeDisplay(charge)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-muted small">No handling charges configured</div>
                                                                        )
                                                                    ) : (
                                                                        <div className="text-center">
                                                                            <div className="h4 text-success">{currentProduct.handling_charges?.length || 0}</div>
                                                                            <small className="text-muted">Handling Slabs</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Products Grid */}
                                {selectedProduct ? (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h5 className="card-title mb-0">
                                                        {currentProduct?.product_name} - Zone Pricing Details
                                                        <small className="text-muted ms-2">
                                                            (Additional Weight: {currentProduct?.additional_weight || 1} kg)
                                                        </small>
                                                    </h5>
                                                </div>
                                                <div className="card-body">
                                                    {filteredPricing.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-striped table-bordered">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>From Zone</th>
                                                                        <th>To Zone</th>
                                                                        <th>Weight Range</th>
                                                                        <th>Base Price (₹)</th>
                                                                        <th>Additional Weight</th>
                                                                        <th>Additional Price (₹)</th>
                                                                        {/* <th>Type</th> */}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredPricing.map((zonePricing, index) => {
                                                                        const baseSlab = zonePricing.standard_pricing.find(isBaseWeightSlab);
                                                                        const additionalSlab = zonePricing.standard_pricing.find(isAdditionalWeightSlab);
                                                                        
                                                                        if (!baseSlab || !additionalSlab) return null;

                                                                        const baseData = extractPricingData(baseSlab);
                                                                        const additionalData = extractPricingData(additionalSlab);

                                                                        return (
                                                                            <tr key={index}>
                                                                                <td>{zonePricing.from_zone.zone_name}</td>
                                                                                <td>{zonePricing.to_zone.zone_name}</td>
                                                                                <td>
                                                                                    {formatWeightRange(baseSlab)}
                                                                                </td>
                                                                                <td>
                                                                                    {baseData.price ? `₹${baseData.price}` : '-'}
                                                                                </td>
                                                                                <td>
                                                                                    {additionalData.additional_weight} kg
                                                                                </td>
                                                                                <td>
                                                                                    {additionalData.additional_price ? `₹${additionalData.additional_price}` : '₹0.00'}
                                                                                </td>
                                                                                {/* <td>
                                                                                    <span className="badge bg-primary">Standard</span>
                                                                                </td> */}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <p className="text-muted">No pricing data found for the selected filters.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Show all products in cards */
                                    <div className="row">
                                      
                                    </div>
                                )}

                                {/* Pricing Explanation */}
                                {selectedProduct && filteredPricing.length > 0 && (
                                    <div className="row mt-4">
                                        <div className="col-12">
                                            <div className="alert alert-info">
                                                <h6 className="alert-heading">Pricing Structure:</h6>
                                                <p className="mb-0 small">
                                                    • <strong>Base Price:</strong> Fixed price for the specified weight range (0-20kg)<br/>
                                                    • <strong>Additional Price:</strong> Per kg rate for weight beyond the base range<br/>
                                                    • <strong>Additional Weight:</strong> The weight increment for additional charges (typically 1kg)<br/>
                                                    • <strong>VAS Charges:</strong> Value Added Services like FSC, Risk Coverage, COD, etc.<br/>
                                                    • <strong>ODA Charges:</strong> Out of Delivery Area charges for remote locations<br/>
                                                    • <strong>Handling Charges:</strong> Additional handling fees based on weight slabs
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                       
                    </div>
                    <Footer /> 
                </div>
            </div>
            
        </div>
    );
}

export default B2B_pricing;